import { useEffect, useMemo, useRef, useState } from 'react';
import { allocateLayout } from './layout';
import { makeOutputFilename } from './filename';
import { buildCombinedPdf, PdfInputError, readOrderFile } from './pdf';
import type { OrderFile, RejectedFile } from './types';

type Status = 'idle' | 'processing' | 'ready' | 'error';
type Preview = { orderId: string; url: string; name: string };

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function App() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeOrders, setActiveOrders] = useState<OrderFile[]>([]);
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const [output, setOutput] = useState<Uint8Array | null>(null);
  const [outputName, setOutputName] = useState('');
  const [downloadWarning, setDownloadWarning] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const loadEpochRef = useRef(0);
  const selectionRef = useRef(0);
  const queueRevisionRef = useRef(0);
  const entrySequenceRef = useRef(0);

  const plans = useMemo(() => allocateLayout(activeOrders.length), [activeOrders.length]);
  const duplicateGroups = useMemo(() => {
    const groups = new Map<string, OrderFile[]>();
    for (const order of activeOrders) {
      const group = groups.get(order.fingerprint) ?? [];
      group.push(order);
      groups.set(order.fingerprint, group);
    }
    return [...groups.values()].filter((group) => group.length > 1);
  }, [activeOrders]);
  const duplicateCount = duplicateGroups.reduce((count, group) => count + group.length - 1, 0);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const clearGeneratedState = () => {
    setOutput(null);
    setOutputName('');
    setDownloadWarning('');
  };

  const invalidateQueueResult = () => {
    queueRevisionRef.current += 1;
    clearGeneratedState();
    setError('');
    setStatus('idle');
  };

  const nextEntryId = () => {
    entrySequenceRef.current += 1;
    return `entry-${Date.now()}-${entrySequenceRef.current}`;
  };

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const epoch = loadEpochRef.current;
    const selection = ++selectionRef.current;
    const entries = files.map((file) => ({ file, id: nextEntryId() }));
    invalidateQueueResult();
    setRejected([]);

    const results = await Promise.all(entries.map(async ({ file, id }) => {
      try {
        return { order: await readOrderFile(file, id) };
      } catch (cause) {
        const rejectedFile: RejectedFile = {
          id,
          name: file.name,
          message: cause instanceof PdfInputError ? cause.message : 'The file could not be loaded.',
        };
        return { rejected: rejectedFile };
      }
    }));

    if (epoch !== loadEpochRef.current) return;
    const accepted = results.flatMap((result) => result.order ? [result.order] : []);
    const failed = results.flatMap((result) => result.rejected ? [result.rejected] : []);
    if (accepted.length > 0) {
      queueRevisionRef.current += 1;
      setActiveOrders((current) => [...current, ...accepted]);
    }
    if (selection === selectionRef.current) setRejected(failed);
  };

  const removeOrder = (id: string) => {
    invalidateQueueResult();
    setActiveOrders((current) => current.filter((order) => order.id !== id));
    if (preview?.orderId === id) {
      URL.revokeObjectURL(preview.url);
      setPreview(null);
    }
  };

  const moveOrder = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= activeOrders.length) return;
    invalidateQueueResult();
    setActiveOrders((current) => {
      if (index >= current.length || target >= current.length) return current;
      const copy = [...current];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      return copy;
    });
  };

  const reset = () => {
    loadEpochRef.current += 1;
    selectionRef.current += 1;
    queueRevisionRef.current += 1;
    setActiveOrders([]);
    setRejected([]);
    clearGeneratedState();
    setError('');
    setStatus('idle');
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const download = (bytes: Uint8Array, name: string) => {
    const copy = new Uint8Array(bytes);
    const url = URL.createObjectURL(new Blob([copy.buffer], { type: 'application/pdf' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const generate = async () => {
    if (activeOrders.length === 0 || status === 'processing') return;
    const revision = queueRevisionRef.current;
    const snapshot = [...activeOrders];
    setStatus('processing');
    setError('');
    try {
      const bytes = await buildCombinedPdf(snapshot);
      if (revision !== queueRevisionRef.current) return;
      let storage: Storage | undefined;
      try {
        storage = window.localStorage;
      } catch {
        storage = undefined;
      }
      const result = makeOutputFilename(new Date(), storage);
      setOutput(bytes);
      setOutputName(result.filename);
      setDownloadWarning(result.warning ?? '');
      setStatus('ready');
      try {
        download(bytes, result.filename);
      } catch {
        setDownloadWarning('The browser blocked the automatic download. Use “Download again” or save the PDF from the browser preview.');
      }
    } catch (cause) {
      if (revision !== queueRevisionRef.current) return;
      setStatus('error');
      setError(cause instanceof Error ? cause.message : 'PDF generation failed. Your original files are still loaded.');
    }
  };

  const openPreview = (order: OrderFile) => {
    if (preview) URL.revokeObjectURL(preview.url);
    const copy = new Uint8Array(order.bytes);
    const url = URL.createObjectURL(new Blob([copy.buffer], { type: 'application/pdf' }));
    setPreview({ orderId: order.id, url, name: order.name });
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">PRIVATE · LOCAL · OFFLINE-READY</p>
          <h1>Daraz PDF Automator</h1>
          <p className="subtitle">Arrange single-order labels into print-ready A5 and A4 sheets.</p>
        </div>
        <div className="privacy-pill"><span className="status-dot" /> Files stay in this browser</div>
      </header>

      <section className="workspace">
        <div className="main-column">
          <section className={`dropzone ${dragActive ? 'drag-active' : ''}`} onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={() => setDragActive(false)} onDrop={(event) => { event.preventDefault(); setDragActive(false); void addFiles([...event.dataTransfer.files]); }}>
            <div className="upload-icon" aria-hidden="true">↑</div>
            <h2>Add order PDFs</h2>
            <p>One single-page Daraz order per file. Your selected order is preserved.</p>
            <button className="button secondary" type="button" onClick={() => inputRef.current?.click()}>Choose PDF files</button>
            <input ref={inputRef} className="visually-hidden" type="file" accept="application/pdf,.pdf" multiple onChange={(event) => { const files = [...(event.target.files ?? [])]; event.currentTarget.value = ''; void addFiles(files); }} />
            <small>Maximum 50 MB per file · drag and drop supported</small>
          </section>

          {rejected.length > 0 && <section className="alert error-alert" role="alert"><strong>Files needing attention</strong>{rejected.map((item) => <div className="rejected" key={item.id}><span title={item.name}>{item.name}</span><span>{item.message}</span></div>)}</section>}
          {duplicateCount > 0 && <section className="alert warning-alert" role="status"><strong>Identical PDF content detected.</strong> {duplicateCount} extra {duplicateCount === 1 ? 'copy remains' : 'copies remain'} in {duplicateGroups.length} group{duplicateGroups.length === 1 ? '' : 's'}. Review and remove individual rows if needed; nothing was discarded automatically.</section>}
          {error && <section className="alert error-alert" role="alert">{error}</section>}

          <section className="orders-panel">
            <div className="section-heading"><div><p className="eyebrow">BATCH QUEUE</p><h2>{activeOrders.length ? `${activeOrders.length} order${activeOrders.length === 1 ? '' : 's'} selected` : 'No orders selected'}</h2></div><span className="count-badge" aria-label={`${activeOrders.length} active orders`}>{activeOrders.length}</span></div>
            {activeOrders.length === 0 ? <div className="empty-state"><div className="empty-mark">01</div><p>Add one or more PDFs to see your print sequence here.</p></div> : <div className="order-list">{activeOrders.map((order, index) => <article className="order-row" key={order.id}><div className="order-index">{String(index + 1).padStart(2, '0')}</div><div className="order-info"><strong title={order.name}>{order.name}</strong><span>{formatBytes(order.size)} · {order.sourceWidth.toFixed(1)} × {order.sourceHeight.toFixed(1)} pt</span></div><div className="row-actions"><button type="button" className="icon-button" onClick={() => openPreview(order)} aria-label={`Preview ${order.name}`}>Preview</button><button type="button" className="icon-button" onClick={() => moveOrder(index, -1)} disabled={index === 0} aria-label="Move up">↑</button><button type="button" className="icon-button" onClick={() => moveOrder(index, 1)} disabled={index === activeOrders.length - 1} aria-label="Move down">↓</button><button type="button" className="icon-button danger" onClick={() => removeOrder(order.id)} aria-label={`Remove ${order.name}`}>×</button></div></article>)}</div>}
          </section>
        </div>

        <aside className="side-column">
          <section className="summary-card"><p className="eyebrow">OUTPUT PLAN</p><div className="plan-number">{plans.length || '—'}</div><div className="plan-label">{plans.length ? `PDF page${plans.length === 1 ? '' : 's'} · mixed sizes allowed` : 'Add orders to calculate'}</div>{plans.length > 0 && <div className="page-plan">{plans.map((plan, index) => <div className="plan-row" key={`${plan.kind}-${index}`}><span className={`paper paper-${plan.kind.toLowerCase()}`}><i /></span><span>Page {index + 1}</span><strong>{plan.kind}</strong><small>{plan.placements.length}/{plan.kind === 'A5' ? 2 : 4} labels</small></div>)}</div>}<div className="rule" /><p className="microcopy">1–2 labels → A5 portrait with sideways labels<br />3–4 labels → A4 portrait, 2 × 2 upright</p></section>
          <button className="button primary generate-button" type="button" disabled={!activeOrders.length || status === 'processing'} onClick={() => void generate()}>{status === 'processing' ? <><span className="spinner" /> Generating locally…</> : 'Generate & download PDF'}</button>
          {status === 'ready' && output && <section className="success-card" role="status"><div className="success-icon">✓</div><strong>Your PDF is ready</strong><span>{outputName}</span><button className="button secondary full" type="button" onClick={() => download(output, outputName)}>Download again</button>{downloadWarning && <small>{downloadWarning}</small>}</section>}
          <button className="reset-button" type="button" onClick={reset} disabled={!activeOrders.length && !output && !rejected.length}>Create another PDF</button>
        </aside>
      </section>

      <footer><span>All processing happens on this device.</span><span>No uploads · No account · No remote PDF services</span></footer>

      {preview && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closePreview(); }}><section className="preview-modal" role="dialog" aria-modal="true" aria-label={`Preview ${preview.name}`}><div className="modal-head"><strong title={preview.name}>{preview.name}</strong><button type="button" className="icon-button" onClick={closePreview}>Close ×</button></div><iframe title={`PDF preview for ${preview.name}`} src={preview.url} /></section></div>}
    </main>
  );
}

export default App;
